<script setup>
import {CashIcon, DotsHorizontalIcon, ChevronDownIcon, MinusIcon, ReceiptRefundIcon, BanIcon} from "@heroicons/vue/outline";
import ButtonPrimary from "../../buttons/buttonPrimary.vue";
import ButtonSecondary from "../../buttons/buttonSecondary.vue";
import {computed, onMounted, reactive, ref} from "vue";
import ButtonWithConfirmation from "../../buttons/buttonWithConfirmation.vue";
import { useNotificationStore } from '@/stores/NotificationStore.js'
const notificationStore = useNotificationStore()

const props = defineProps({
    order: {
        type: Object,
        required: true,
    },
    displaymode: {
        type: String,
        required: false,
        default: 'listitem',
    }
})

const expanded = ref(false)
const _order = ref(props.order)

const paymentStateUpdateState = ref('')
const productsDeliveredUpdateState = ref('')
const syncingRefunds = ref(false)
const cancellingSubscriptionState = ref('')
const editingPayment = ref(null)
const savingPaymentState = ref('')
const paymentForm = reactive({
    total_paid: 0,
    total_paid_vat: 0,
})

const paymentsNewestFirst = computed(() => [...(_order.value.payments ?? [])].reverse())

// When the purchased products' access ends or ended; empty while access has no end
const accessEndText = computed(() => {
    if (! _order.value.access_ends_at_display) {
        return ''
    }

    return _mft(
        _order.value.access_ended ? 'invoiceLink:purchase.accessEndedOn' : 'invoiceLink:purchase.accessEndsOn',
        {date: _order.value.access_ends_at_display},
    )
})

const reloadOrder = async () => {
    try {
        const res = await axios.get(route('api.invoice-links.orders.show', _order.value.uuid))
        _order.value = res.data.order
    } catch (e) {
        // The state change already succeeded; the rest refreshes on the next page load
    }
}

const setPurchaseState = async (state) => {
    if (paymentStateUpdateState.value === 'waiting') {return;}
    paymentStateUpdateState.value = 'waiting'

    const res = await axios.post(route('api.invoice-links.set-payment-state', {
        uuid: _order.value.uuid,
        state: state,
    }))

    if (res.data.success) {
        _order.value.state = state
        paymentStateUpdateState.value = 'success'

        // Cancelling a subscription sets when its access ends
        if (state === 'cancelled') {
            await reloadOrder()
        }
    } else {
        paymentStateUpdateState.value = 'error'
        notificationStore.addNotification({
            type: 'error',
            message: res.data.error,
            dismissAfter: 5000,
        })
    }
}

const deliverLicenses = async () => {
    if (productsDeliveredUpdateState.value === 'waiting') {return;}
    productsDeliveredUpdateState.value = 'waiting'

    let res

    try {
        res = await axios.post(route('api.invoice-links.deliver-licenses', _order.value.uuid))
    } catch (e) {
        productsDeliveredUpdateState.value = 'error'
        return
    }

    if (res.data.success) {
        _order.value.products_delivered = true
        productsDeliveredUpdateState.value = 'success'
    } else {
        productsDeliveredUpdateState.value = 'error'
    }
}

const markAsPaidAndDeliverLicenses = () => {
    setPurchaseState('paid')
    deliverLicenses()
}

const syncRefundsForOrder = async () => {
    if (syncingRefunds.value) return
    syncingRefunds.value = true
    try {
        await axios.post(route('api.invoice-links.orders.sync-refunds'), { purchase_uuid: _order.value.uuid })
        notificationStore.addNotification({
            type: 'success',
            message: _mft('invoiceLink:purchase.action.syncRefunds.success') || 'Refund sync scheduled',
            dismissAfter: 4000,
        })
    } catch (e) {
        notificationStore.addNotification({
            type: 'error',
            message: _mft('invoiceLink:purchase.action.syncRefunds.error') || 'Failed to sync refunds',
            dismissAfter: 6000,
        })
    } finally {
        syncingRefunds.value = false
    }
}

const cancelSubscription = async () => {
    if (cancellingSubscriptionState.value === 'waiting') {return;}
    cancellingSubscriptionState.value = 'waiting'

    try {
        const res = await axios.post(route('api.invoice-links.orders.cancel-subscription', {
            uuid: _order.value.uuid,
        }))

        if (res.data?.success) {
            _order.value = res.data.order ?? {
                ..._order.value,
                state: 'cancelled',
            }
            cancellingSubscriptionState.value = 'success'

            notificationStore.addNotification({
                type: 'success',
                message: _mft('invoiceLink:purchase.state.cancelled'),
                dismissAfter: 4000,
            })
        } else {
            cancellingSubscriptionState.value = 'error'
            notificationStore.addNotification({
                type: 'error',
                message: res.data?.error || res.data?.message || _mft('error:error.savingTryAgain') || 'Failed to cancel subscription',
                dismissAfter: 6000,
            })
        }
    } catch (e) {
        cancellingSubscriptionState.value = 'error'
        notificationStore.addNotification({
            type: 'error',
            message: e.response?.data?.error || e.response?.data?.message || _mft('error:error.savingTryAgain') || 'Failed to cancel subscription',
            dismissAfter: 6000,
        })
    }
}

const formatAmountForInput = (amount, fallback = 0) => {
    const numericAmount = Number(amount ?? fallback)

    if (Number.isNaN(numericAmount)) {
        return 0
    }

    return Math.round(numericAmount * 100) / 100
}

const openPaymentEditor = (payment) => {
    editingPayment.value = payment
    paymentForm.total_paid = formatAmountForInput(payment.nominal_total_paid ?? payment.nominal_installment_amount)
    paymentForm.total_paid_vat = formatAmountForInput(payment.nominal_total_paid_vat)
    savingPaymentState.value = ''
}

const closePaymentEditor = () => {
    editingPayment.value = null
    savingPaymentState.value = ''
    paymentForm.total_paid = 0
    paymentForm.total_paid_vat = 0
}

const savePayment = async () => {
    if (! editingPayment.value) {
        return
    }
    if (savingPaymentState.value === 'waiting') {return;}

    savingPaymentState.value = 'waiting'

    try {
        const res = await axios.put(route('api.invoice-links.orders.payments.update', {
            uuid: _order.value.uuid,
            payment_id: editingPayment.value.id,
        }), {
            total_paid: Number(paymentForm.total_paid) || 0,
            total_paid_vat: Number(paymentForm.total_paid_vat) || 0,
        })

        if (res.data?.success) {
            _order.value = res.data.order
            savingPaymentState.value = 'success'
            closePaymentEditor()
        } else {
            savingPaymentState.value = 'error'
            notificationStore.addNotification({
                type: 'error',
                message: _mft('error:error.saving') || 'Could not save payment',
                dismissAfter: 5000,
            })
        }
    } catch (e) {
        savingPaymentState.value = 'error'
        notificationStore.addNotification({
            type: 'error',
            message: _mft('error:error.savingTryAgain') || 'Saving failed, please try again',
            dismissAfter: 5000,
        })
    }
}

onMounted(() => {
    if (props.displaymode === 'expanded') {
        expanded.value = true
    }
})

</script>
<template>
    <div
        class="flex flex-col md:flex-row md:items-center md:justify-between"
        :class="expanded ? '' : 'hover:bg-gray-50'"
    >
        <div class="flex space-x-3">
            <div>
                <span
                    v-if="_order.state === 'initiated'"
                    class="text-xs text-gray-500"
                    :title="_mft('invoiceLink:purchase.state.initiated')"
                >
                    <DotsHorizontalIcon class="h-6 w-6" />
                </span>
                <span
                    v-if="_order.state === 'paid'"
                    class="text-xs text-myflowGreen"
                    :title="_mft('invoiceLink:purchase.state.paid')"
                >
                    <CashIcon class="h-6 w-6" />
                </span>
                <span
                    v-if="_order.state === 'cancelled'"
                    class="text-xs text-gray-500"
                    :title="_mft('invoiceLink:purchase.state.cancelled')"
                >
                    <BanIcon class="h-6 w-6" />
                </span>
                <span
                    v-if="_order.state === 'credited'"
                    class="text-xs text-gray-500"
                    :title="_mft('invoiceLink:purchase.state.credited')"
                >
                    <ReceiptRefundIcon class="h-6 w-6" />
                </span>
            </div>
            <div class="block">
                <a
                    :href="route('invoice-links.orders.show', _order.uuid)"
                >
                    <span class="text-gray-400 mr-2">
                        <span class="text-gray-300">#</span>
                        <span class="select-all cursor-default">{{ _order.invoice_reference }}</span>
                    </span>
                    {{ _order.title }}
                </a>
                <div class="space-y-1">
                    <div class="flex items-center space-x-4">
                        <div class="w-32">
                            <span class="text-xs">{{ _order.created_at_user_timezone_display }}</span>
                        </div>
                        <div class="w-32">
                            <span class="text-xs">{{ _mft('invoiceLink:purchase.method.' + _order.payment_method) }}</span>
                        </div>
                    </div>
                </div>
                <div class="text-xs">

                </div>
            </div>
        </div>
        <div class="mt-0 flex justify-between lg:justify-end space-x-4">
            <div class="flex flex-col items-end text-xs space-y-1 pl-9 pt-1 lg:pl-0 lg:pt-0">
                <div>{{ _order.nominal_total_amount }} {{ _order.currency}}</div>
                <div v-if="_order.total_refunded && _order.total_refunded > 0" class="text-gray-500">
                    {{ _mft('invoiceLink:purchase.refunded') || 'Refunded' }}:
                    {{ _order.nominal_total_refunded }} {{ _order.currency}}
                </div>

            </div>
            <div v-if="props.displaymode !== 'expanded'">
                <button-secondary
                    size="sm"
                    @click="expanded = !expanded"
                >
                    <ChevronDownIcon
                        v-if="!expanded"
                        class="h-6 w-6"
                    />
                    <MinusIcon
                        v-if="expanded"
                        class="h-6 w-6"
                    />
                </button-secondary>
            </div>
        </div>
    </div>
    <div
        v-if="expanded"
        class="gap-y-2 p-4 md:p-6"
        :class="props.displaymode === 'listitem' ? 'bg-gradient-to-b from-white to-gray-100' : ''"
    >
        <div :class="props.displaymode === 'listitem' ? 'grid' : 'grid'" class=" lg:grid-cols-3 divide-x">
            <div class="mb-4">
                <h3 class="mb-2">{{ _mft('invoiceLink:purchase.customer.title') }}</h3>
                <input-label :label="_mft('invoiceLink:purchase.billingInformation.label')"></input-label>
                <p>
                    {{ _order.billing_name }}<br>
                    {{ _order.billing_email }}<br>
                    {{ _order.billing_phone }}
                </p>

                <p v-if="_order.billing_company || _order.billing_identityno">
                    {{ _order.billing_company }}<br>
                    {{ _order.billing_identityno }}
                </p>

                <p>
                    {{ _order.billing_address }}<br>
                    {{ _order.billing_zip }} {{ _order.billing_city }}<br>
                    {{ _order.billing_state }} {{ _order.billing_country }}<br>
                </p>
            </div>
            <div class="px-4">
                <div class="flex items-center space-x-2 mb-2">
                    <h3>{{ _mft('invoiceLink:purchase.order.title') }}</h3>
                    <pill
                        v-if="! _order.products_delivered"
                        size="sm"
                        class="bg-gray-500 text-white cursor-default"
                    >
                        {{ _mft('invoiceLink:purchase.productsDelivered.no') }}
                    </pill>
                    <pill
                        v-if="_order.products_delivered"
                        size="sm"
                        class="bg-myflowGreen text-white cursor-default"
                    >
                        {{ _mft('invoiceLink:purchase.productsDelivered.yes') }}
                    </pill>
                </div>
                <card-sub>
                    <ul>
                        <li
                            v-for="(item, i) in _order.order_items"
                            :key="i"
                            :class="item.subitem ? 'text-xs' : 'mt-2 first:mt-0'"
                        >
                            <div>
                                <div :class="item.subitem ? '' : 'font-bold'">{{ item.title }}</div>
                                <div
                                    v-if="item.quantity > 1"
                                    class="text-gray-500"
                                >{{ item.quantity }} {{ item.unit }}</div>
                            </div>
                        </li>
                    </ul>
                </card-sub>

                <div
                    v-if="accessEndText"
                    class="mt-1 text-right text-xs text-gray-500"
                >
                    {{ accessEndText }}
                </div>

                <div class="flex justify-end mt-2">
                    <button-with-confirmation
                        v-if="! _order.products_delivered"
                        size="sm"
                        :state="productsDeliveredUpdateState"
                        style-type="primary"
                        confirm-type="primary"
                        :confirm-text="_mft('invoiceLink:purchase.productsDelivered.action.confirmationText')"
                        :confirm-button-label="_mft('invoiceLink:purchase.productsDelivered.action.deliver')"
                        :abort-button-label="_mft('shared:action.cancel')"
                        @confirmed="deliverLicenses"
                    >
                        {{ _mft('invoiceLink:purchase.productsDelivered.action.deliver') }}
                    </button-with-confirmation>
                </div>
            </div>

            <div class="pl-4">
                <div class="flex items-center space-x-2 mb-2">
                    <h3>{{ _mft('invoiceLink:purchase.billing.title') }}</h3>
                    <pill
                        v-if="_order.state === 'initiated'"
                        size="sm"
                        class="bg-gray-500 text-white cursor-default"
                    >
                        {{ _mft('invoiceLink:purchase.state.initiated') }}
                    </pill>
                    <pill
                        v-if="_order.state === 'paid'"
                        size="sm"
                        class="bg-myflowGreen text-white cursor-default"
                    >
                            <span v-if="_order.completed_installments !== _order.total_installments">
                                {{ _order.completed_installments }}/{{ _order.total_installments }}
                            </span>
                        {{ _mft('invoiceLink:purchase.state.paid') }}
                    </pill>
                </div>

                <div class="flex justify-between space-x-2">
                    <input-label :label="_mft('invoiceLink:purchase.totalAmount')"></input-label>
                    <span>{{ _order.nominal_total_amount }} {{ _order.currency}}</span>
                </div>

                <div
                    class="flex justify-between space-x-2"
                    v-if="_order.vat_rule !== 'stripe_auto_tax' && _order.total_vat > 0"
                >
                    <input-label :label="_mft('invoiceLink:purchase.totalVat')"></input-label>
                    <span>{{ _order.nominal_total_vat }} {{ _order.currency}}</span>
                </div>

                <div
                    class="flex justify-between space-x-2"
                    v-if="_order.vat_rule !== 'stripe_auto_tax' && _order.total_vat > 0"
                >
                    <input-label :label="_mft('invoiceLink:purchase.totalAmountInclVat')"></input-label>
                    <span>{{ _order.nominal_total_amount_incl_vat }} {{ _order.currency}}</span>
                </div>

                <div
                    v-if="_order.recurring"
                    class="italic text-gray-400"
                >
                    {{ _mft('invoiceLink:recurringPayments.monthly') }}
                </div>

                <div
                    v-if="_order.total_installments > 1"
                    class="italic text-gray-400"
                >
                    {{ _order.total_installments }} {{ _mft('invoiceLink:instalments').toLowerCase() }}
                </div>

                <div class="flex justify-between space-x-2 mt-4">
                    <input-label :label="_mft('invoiceLink:purchase.state.label')"></input-label>
                    <span>
                            {{ _mft('invoiceLink:purchase.state.' + _order.state) }}
                        </span>
                </div>
                <div class="flex justify-between space-x-2" v-if="_order.total_refunded && _order.total_refunded > 0">
                    <input-label :label="_mft('invoiceLink:purchase.refunded') || 'Refunded'"></input-label>
                    <span>
                            {{ _order.nominal_total_refunded }} {{ _order.currency}}
                        </span>
                </div>
                <div class="flex justify-between space-x-2">
                    <input-label :label="_mft('invoiceLink:purchase.paymentMethod.label')"></input-label>
                    <span>
                            {{ _mft('invoiceLink:purchase.method.' + _order.payment_method) }}
                        </span>
                </div>
<!--                <div class="flex justify-between space-x-2">-->
<!--                    <input-label :label="_mft('invoiceLink:purchase.invoiceReference.label')"></input-label>-->
<!--                    <span>-->
<!--                            <a-->
<!--                                :href="route('invoice-link.purchase.receipt.show', _order.uuid)"-->
<!--                                target="_blank"-->
<!--                                class="mf-link"-->
<!--                            >-->
<!--                                {{ _order.invoice_reference }}-->
<!--                            </a>-->
<!--                        </span>-->
<!--                </div>-->
            </div>
        </div>
        <div class="flex justify-end space-x-2 mt-4">
            <button-with-confirmation
                v-if="_order.payment_method === 'invoice' && _order.state === 'initiated'"
                size="sm"
                confirm-type="primary"
                :confirm-text="_mft('invoiceLink:purchase.state.action.markAsPaid.confirmationText')"
                :confirm-button-label="_mft('invoiceLink:purchase.state.action.markAsPaid')"
                :abort-button-label="_mft('shared:action.cancel')"
                @confirmed="setPurchaseState('paid')"
            >
                {{ _mft('invoiceLink:purchase.state.action.markAsPaid') }}
            </button-with-confirmation>

            <button-with-confirmation
                v-if="_order.payment_method === 'invoice' && _order.state === 'initiated' && ! _order.products_delivered"
                size="sm"
                confirm-type="primary"
                :confirm-text="_mft('invoiceLink:purchase.productsDelivered.action.confirmationText')"
                :confirm-button-label="_mft('invoiceLink:purchase.state.action.markAsPaidAndDeliver')"
                :abort-button-label="_mft('shared:action.cancel')"
                @confirmed="markAsPaidAndDeliverLicenses"
            >
                {{ _mft('invoiceLink:purchase.state.action.markAsPaidAndDeliver') }}
            </button-with-confirmation>

            <button-secondary
                v-if="_order.payment_method === 'invoice' && _order.state === 'paid'"
                size="sm"
                @click="setPurchaseState('initiated')"
            >
                {{ _mft('invoiceLink:purchase.state.action.markAsNotPaid') }}
            </button-secondary>

            <button-with-confirmation
                v-if="_order.payment_method === 'invoice' && _order.state === 'initiated'"
                size="sm"
                style-type="danger"
                confirm-type="danger"
                :confirm-text="_mft('invoiceLink:purchase.state.action.cancel.confirmationText')"
                :confirm-button-label="_mft('shared:action.continue')"
                :abort-button-label="_mft('shared:action.cancel')"
                @confirmed="setPurchaseState('cancelled')"
            >
                {{ _mft('invoiceLink:purchase.state.action.cancel') }}
            </button-with-confirmation>
            <button-with-confirmation
                v-if="_order.payment_method === 'invoice' && _order.state === 'paid'"
                size="sm"
                confirm-type="danger"
                :confirm-text="_mft('invoiceLink:purchase.state.action.credit.confirmationText')"
                :confirm-button-label="_mft('shared:action.continue')"
                :abort-button-label="_mft('shared:action.cancel')"
                @confirmed="setPurchaseState('credited')"
            >
                {{ _mft('invoiceLink:purchase.state.action.credit') }}
            </button-with-confirmation>

            <button-secondary
                v-if="_order.payment_method === 'stripe'"
                size="sm"
                :disabled="syncingRefunds"
                @click="syncRefundsForOrder"
            >
                {{ _mft('invoiceLink:purchase.action.syncRefundsForOrder') || 'Sync refunds for this order' }}
            </button-secondary>

            <button-with-confirmation
                v-if="_order.recurring && _order.total_installments === 1 && _order.state !== 'cancelled'"
                size="sm"
                style-type="danger"
                confirm-type="danger"
                :state="cancellingSubscriptionState"
                :confirm-text="_mft('shared:confirm.areYouSure')"
                :confirm-button-label="_mft('shared:confirm.positive')"
                :abort-button-label="_mft('shared:confirm.negative')"
                @confirmed="cancelSubscription"
            >
                {{ _mft('shared:cancel') }}
            </button-with-confirmation>
        </div>

        <div class="col-span-3 mt-4">
            <card-sub>
                <div v-if="_order.payments && _order.payments.length">
                    <h3 class="mb-2">{{ _mft('invoiceLink:purchase.installments') || 'Installments' }}</h3>
                    <ul class="divide-y divide-gray-200 -mt-1">
                        <li class="grid grid-cols-7 gap-2 text-xs text-gray-400">
                            <div class="col-span-2">
                                {{ _mft('invoiceLink:purchase.invoiceReference.short') }}
                            </div>
                            <div class="col-span-2">
                                {{ _mft('invoiceLink:purchase.totalAmount') }}
                            </div>
                            <div class="col-span-2">
                                {{ _mft('invoiceLink:purchase.paidAmount') }}
                            </div>
                            <div>
                            </div>
                        </li>

                        <li
                            v-for="(p, i) in paymentsNewestFirst"
                            :key="p.id"
                            class="grid grid-cols-7 gap-2 text-xs py-1"
                            :data-payment-id="p.id"
                        >
                            <div class="col-span-2">
                                {{ p.reference }}
                            </div>
                            <div class="col-span-2">
                                {{ p.nominal_installment_amount }} {{ _order.currency }}
                            </div>
                            <div class="col-span-2">
                                {{ p.nominal_total_paid }} {{ _order.currency }}
                                <div class="text-gray-500 text-xxs">
                                    ({{ _mft('invoiceLink:purchase.exclVat', {vat: p.nominal_total_paid_vat + ' ' + _order.currency}) }})
                                </div>
                            </div>
                            <div class="flex flex-col items-end space-y-1 text-right">
                                <div
                                    v-if="_order.payment_method === 'invoice'"
                                    class="flex items-center space-x-1"
                                >
                                    <span>{{ _mft('invoiceLink:purchase.state.'+p.state) || p.state }}</span>
                                    <button-secondary
                                        :style="p.state === 'paid' ? 'primary' : 'default'"
                                        size="xs"
                                        @click="openPaymentEditor(p)"
                                    >
                                        <i class="fa-regular fa-pen text-gray-600"></i>
                                    </button-secondary>
                                </div>
                                <span
                                    v-else
                                    class="bg-white border rounded-md text-xxs px-1 py-px text-gray-500"
                                >
                                    {{ _mft('invoiceLink:purchase.state.'+p.state) || p.state }}
                                </span>

                                <a
                                    :href="route('invoice-link.purchase.payment.receipt.show', [order.uuid, p.id])+'?media=screen'"
                                    class="text-xxs flex space-x-1 items-center"
                                    target="_blank"
                                >
                                    <span class="mf-link">
                                        {{ p.state === 'paid' ? _mft('receipt:domainNameSingular') : _mft('invoiceLink:invoice') }}
                                    </span>
                                    <i class="fa-regular fa-arrow-up-right-from-square"></i>
                                </a>
                            </div>
                            <div v-if="p.total_refunded > 0" class="col-span-7 font-bold">
                                {{ _mft('invoiceLink:purchase.refunded') }}: {{ p.nominal_total_refunded }} {{ _order.currency }}
                            </div>
                        </li>
                    </ul>
                </div>
            </card-sub>
        </div>
    </div>
    <slideout-with-slots
        v-if="editingPayment"
        @close="closePaymentEditor"
        max-width-class="max-w-lg"
    >
        <template #title>
            <div class="space-y-1">
                <h2>{{ _mft('invoiceLink:purchase.state.action.markAsPaid') || 'Update payment' }}</h2>
                <p class="text-sm">
                    {{ _mft('invoiceLink:purchase.invoiceReference.label') || 'Invoice' }} #{{ _order.invoice_reference }}
                </p>
            </div>
        </template>
        <template #body>
            <form
                class="space-y-4"
                @submit.prevent="savePayment"
            >
                <div>
                    <input-number
                        v-model:content="paymentForm.total_paid"
                        :label="_mft('invoiceLink:purchase.paidAmount') + ' ('+_mft('invoiceLink:purchase.exclVat', {vat: paymentForm.total_paid_vat+' '+_order.currency})+')' || 'Paid amount excl. VAT'"
                        input-class="w-full"
                        step="0.01"
                        min="0"
                    />
                </div>
                <div>
                    <input-number
                        v-model:content="paymentForm.total_paid_vat"
                        :label="_mft('invoiceLink:purchase.totalVat') || 'VAT paid'"
                        input-class="w-full"
                        step="0.01"
                        min="0"
                    />
                </div>
                <div class="flex items-center justify-end space-x-2 pt-2">
                    <button-secondary
                        type="button"
                        size="sm"
                        @click="closePaymentEditor"
                    >
                        {{ _mft('shared:action.cancel') }}
                    </button-secondary>
                    <button-primary
                        type="submit"
                        size="sm"
                        :disabled="savingPaymentState === 'waiting'"
                    >
                        <span v-if="savingPaymentState === 'waiting'">{{ _mft('shared:save.saving') || 'Saving...' }}</span>
                        <span v-else>{{ _mft('shared:action.save') || 'Save' }}</span>
                    </button-primary>
                </div>
            </form>
        </template>
    </slideout-with-slots>
</template>
